import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import CommonModalLayout from '../Layouts/CommonModalLayout';
import { buttonStyles, inputStyles, authModalStyles } from '../../styles/commonStyles';
import ErrorMessage from '../Common/ErrorMessage';
import { FaMagic } from 'react-icons/fa';
import { register, login } from '../../api/auth';
import { useDispatch } from 'react-redux';
import { setUser } from '../../redux/slices/authSlice';
import { toast } from 'react-toastify';

const RegisterModal = ({ isOpen, onClose, onLoginClick, isAuthPage = false }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [email, setEmail] = useState('');
    const [login, setLogin] = useState('');
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [agreement, setAgreement] = useState(false);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    // Автоматически генерируем логин из email
    useEffect(() => {
        if (email) {
            const emailLogin = email.split('@')[0];
            setLogin(emailLogin);
        }
    }, [email]);

    const generatePassword = () => {
        const length = 12;
        const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
        let newPassword = "";
        for (let i = 0; i < length; i++) {
            newPassword += charset.charAt(Math.floor(Math.random() * charset.length));
        }
        setPassword(newPassword);
    };

    const validateForm = () => {
        if (login.length < 3) {
            setError(t('auth.errors.loginTooShort'));
            return false;
        }
        if (login.length > 50) {
            setError(t('auth.errors.loginTooLong'));
            return false;
        }
        if (password.length < 8) {
            setError(t('auth.errors.passwordTooShort'));
            return false;
        }
        if (!agreement) {
            setError(t('auth.errors.agreementRequired'));
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        
        if (!validateForm()) {
            return;
        }

        setIsLoading(true);
        try {
            const userData = {
                email,
                login,
                username: username || null,
                password,
                agreement,
                auth_type: 'local'
            };

    
            const response = await register(userData);

            
            // Автоматический вход после регистрации
            try {
                const loginResponse = await login({
                    login: login,
                    password: password
                });
                
                // Сохраняем данные пользователя в localStorage
                localStorage.setItem('user', JSON.stringify(loginResponse));
                
                // Обновляем состояние авторизации
                dispatch(setUser(loginResponse));
                
                // Показываем уведомление об успешной регистрации
                toast.success(t('auth.registrationSuccess'));
                
                handleClose();
            } catch (loginError) {
                console.error('Auto login error:', loginError);
                // Если автоматический вход не удался, просто закрываем окно
                handleClose();
                // Показываем уведомление об успешной регистрации
                toast.success(t('auth.registrationSuccess'));
            }
        } catch (err) {
            console.error('Registration error:', err);
            
            // Маппинг ошибок с бэкенда на ключи локализации
            const errorMapping = {
                'Email already registered': 'auth.errors.emailAlreadyRegistered',
                'Login already taken': 'auth.errors.loginAlreadyTaken'
            };
            
            if (err.response?.status === 500) {

                setError(t('auth.errors.internalServerError'));
            } else if (err.response?.status === 400) {
                const errorDetail = typeof err.response.data === 'string' ? err.response.data : err.response.data?.detail;

                const errorKey = errorMapping[errorDetail] || 'auth.errors.registrationFailed';

                setError(t(errorKey));
            } else {

                setError(t('auth.errors.registrationFailed'));
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        // На странице авторизации НЕ позволяем закрыть модальное окно
        if (isAuthPage) {
            console.log('🔐 RegisterModal: Попытка закрыть модальное окно заблокирована');
            return;
        }
        
        setEmail('');
        setLogin('');
        setUsername('');
        setPassword('');
        setAgreement(false);
        setError('');
        onClose();
    };

    const handleBackToLogin = () => {
        setEmail('');
        setLogin('');
        setUsername('');
        setPassword('');
        setAgreement(false);
        setError('');
        onLoginClick();
    };

    return (
        <CommonModalLayout
            isOpen={isOpen}
            onClose={handleClose}
            title={t('auth.registerTitle')}
            closeOnOverlayClick={!isAuthPage} // Блокируем закрытие по клику на оверлей на странице авторизации
            customStyles={authModalStyles}
            showCloseButton={!isAuthPage} // Скрываем кнопку закрытия на странице авторизации
            hideOverlay={isAuthPage} // Скрываем темный оверлей на странице авторизации
        >
            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder={t('auth.emailRegisterPlaceholder')}
                        className={`${inputStyles} ${error && error.includes('email') ? 'border-red-500 dark:border-red-500' : ''}`}
                        required
                    />
                </div>
                <div>
                    <input
                        type="text"
                        value={login}
                        onChange={(e) => setLogin(e.target.value)}
                        placeholder={t('auth.loginPlaceholder')}
                        className={`${inputStyles} ${error && error.includes('login') ? 'border-red-500 dark:border-red-500' : ''}`}
                        required
                        minLength={3}
                        maxLength={50}
                    />
                </div>
                <div>
                    <input
                        type="text"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        placeholder={t('auth.usernamePlaceholder')}
                        className={`${inputStyles} ${error && error.includes('username') ? 'border-red-500 dark:border-red-500' : ''}`}
                    />
                </div>
                <div className="relative">
                    <input
                        type="text"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder={t('auth.passwordPlaceholder')}
                        className={`${inputStyles} ${error && error.includes('password') ? 'border-red-500 dark:border-red-500' : ''} pr-12`}
                        required
                        minLength={8}
                    />
                    <div className="absolute top-0 right-0 h-10 flex items-center pr-3">
                        <button
                            type="button"
                            onClick={generatePassword}
                            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 transition-colors rounded-full dark:hover:bg-gray-700"
                            title={t('auth.generatePassword')}
                        >
                            <FaMagic className="w-4 h-4 mt-0.5" />
                        </button>
                    </div>
                </div>
                <div className="flex items-center">
                    <input
                        type="checkbox"
                        id="agreement"
                        checked={agreement}
                        onChange={(e) => setAgreement(e.target.checked)}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                    />
                    <label htmlFor="agreement" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                        {t('auth.agreementText')}{' '}
                        <a href="/terms" target="_blank" className="text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300">
                            {t('auth.agreementLink')}
                        </a>
                    </label>
                </div>

                <ErrorMessage message={error} />

                <div className="flex flex-col space-y-2">
                    <div className="flex justify-end space-x-2">
                        <button 
                            type="button" 
                            onClick={handleBackToLogin}
                            className={buttonStyles}
                            disabled={isLoading}
                        >
                            {t('auth.backButton')}
                        </button>
                        <button 
                            type="submit" 
                            className={`${buttonStyles} ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                            disabled={isLoading}
                        >
                            {isLoading ? t('auth.registering') : t('auth.registerButton')}
                        </button>
                    </div>
                    <div className="text-sm text-center pt-2 border-t border-gray-200 dark:border-gray-700">
                        <span className="text-blue-600 dark:text-gray-400">
                            {t('auth.haveAccount')}{' '}
                        </span>
                        <button
                            type="button"
                            onClick={handleBackToLogin}
                            className="text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
                        >
                            {t('auth.loginLink')}
                        </button>
                    </div>
                </div>
            </form>
        </CommonModalLayout>
    );
};

export default RegisterModal; 